import { Link, useSearchParams } from "react-router-dom";
import { Brain, FileText, Megaphone, ShieldCheck, Target, UserRound } from "lucide-react";
import Layout from "../components/Layout";
import { useSEO } from "../hooks/useSEO";
import { aiPackages, aiServices } from "../data/aiWorkforceData";

const iconMap = {
  "AI Social Media Manager": Megaphone,
  "AI Executive Assistant": UserRound,
  "AI Blogger / SEO Writer": FileText,
  "AI Sales Scout": Target,
  "AI Outreach Rep": Brain,
  "AI Compliance Assistant": ShieldCheck,
};

export default function AIWorkforce() {
  // Apply SEO meta tags for AI services
  useSEO('aiWorkforce');
  
  const [searchParams] = useSearchParams();

  return (
    <Layout>
      <main className="min-h-screen bg-neutral-950 text-white pt-20">
        {/* Hero */}
        <section className="border-b border-white/10">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 lg:px-12">
            <div className="max-w-4xl">
              <div className="text-sm uppercase tracking-[0.2em] text-white/40">AI Workforce</div>
              <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl lg:text-6xl">
                Managed AI services
                <span className="block bg-gradient-to-r from-sky-300 to-emerald-300 bg-clip-text text-transparent">
                  built for beverage brands.
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-white/70">
                BevRelay combines beverage-industry logic with AI-enabled delivery. Use monthly service packages
                to support sales, content, operations, outreach, and compliance workflows without hiring a full team.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/request-access?service=ai-workforce"
                  className="rounded-2xl bg-white px-6 py-3 font-medium text-neutral-950 transition hover:scale-[1.01]"
                  data-testid="ai-get-started-btn"
                >
                  Get Started
                </Link>
                <a
                  href="#packages"
                  className="rounded-2xl border border-white/10 bg-white/5 px-6 py-3 font-medium transition hover:bg-white/10"
                >
                  View Pricing
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="mx-auto max-w-7xl px-6 py-14 md:px-10 lg:px-12">
          <div className="max-w-2xl">
            <div className="text-sm uppercase tracking-[0.2em] text-white/40">Services</div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
              Choose one function or build your own AI team.
            </h2>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {aiServices.map((service) => {
              const Icon = iconMap[service.name] || Brain;

              return (
                <div
                  key={service.slug}
                  className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
                  data-testid={`service-${service.slug}`}
                >
                  <div className="inline-flex rounded-2xl border border-white/10 bg-white/5 p-3">
                    <Icon className="h-5 w-5 text-sky-300" />
                  </div>

                  <div className="mt-5 text-sm uppercase tracking-[0.18em] text-white/40">
                    {service.category}
                  </div>
                  <h3 className="mt-2 text-2xl font-semibold">{service.name}</h3>
                  <p className="mt-3 leading-7 text-white/65">{service.description}</p>

                  <div className="mt-5 text-sm font-medium text-emerald-300">
                    Starting at {service.monthlyFrom}
                  </div>

                  <div className="mt-6">
                    <div className="text-sm font-medium text-white/80">Outcomes</div>
                    <ul className="mt-3 space-y-2 text-sm text-white/65">
                      {service.outcomes.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6">
                    <div className="text-sm font-medium text-white/80">Deliverables</div>
                    <ul className="mt-3 space-y-2 text-sm text-white/65">
                      {service.deliverables.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/request-access?service=${service.slug}`}
                    className="mt-8 inline-flex rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-medium transition hover:bg-white/10"
                  >
                    Request This Service
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        {/* Packages */}
        <section id="packages" className="border-y border-white/10 bg-white/[0.03]">
          <div className="mx-auto max-w-7xl px-6 py-14 md:px-10 lg:px-12">
            <div className="max-w-3xl">
              <div className="text-sm uppercase tracking-[0.2em] text-white/40">Packages</div>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                Monthly beverage AI packages
              </h2>
              <p className="mt-4 text-lg text-white/70">
                Start with one service or combine multiple functions into a managed support stack.
              </p>
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {aiPackages.map((pkg) => (
                <div
                  key={pkg.name}
                  className={`rounded-3xl border p-6 ${
                    pkg.featured
                      ? "border-sky-400/40 bg-gradient-to-b from-sky-400/10 to-white/[0.04]"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                >
                  <div className="text-sm uppercase tracking-[0.18em] text-white/45">{pkg.name}</div>
                  <div className="mt-4 text-4xl font-semibold">{pkg.price}</div>
                  <p className="mt-4 text-white/65">{pkg.description}</p>

                  <ul className="mt-6 space-y-3 text-white/75">
                    {pkg.includes.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>

                  <Link
                    to={`/request-access?package=${encodeURIComponent(pkg.name)}`}
                    className={`mt-8 inline-flex rounded-2xl px-5 py-3 font-medium transition ${
                      pkg.featured 
                        ? "bg-white text-neutral-950 hover:bg-white/90" 
                        : "border border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    Choose {pkg.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why This Works */}
        <section className="mx-auto max-w-7xl px-6 py-14 md:px-10 lg:px-12">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-sky-400/10 to-emerald-400/10 p-8">
            <div className="max-w-3xl">
              <div className="text-sm uppercase tracking-[0.2em] text-white/40">Why this works</div>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight">
                Generic AI is weak. Beverage-specific AI is stronger.
              </h2>
              <p className="mt-4 text-lg leading-8 text-white/70">
                The advantage is not just AI. The advantage is beverage-specific workflow logic:
                buyers, distributors, tastings, packaging, launches, retailer outreach, compliance prep,
                and structured follow-up.
              </p>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
